import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// The email buttons point at:
//   /request-approval/approve/:token
//   /request-approval/reject/:token
//   /request-approval/view/:token
//
// Legacy links under /attendance/request/... are also still routed here.
//
// This page mounts at all three URLs and:
//   1. Reads the token + action from the URL
//   2. Calls the corresponding public backend endpoint
//   3. Renders a clean success / error card based on the result
//
// No login required — the signed HMAC token is the authentication.

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5030/api';

const SEVERITY = {
  approved: { bg: '#DCFCE7', fg: '#166534', border: '#86EFAC', icon: '✓', title: 'Request Approved Successfully' },
  rejected: { bg: '#FEE2E2', fg: '#991B1B', border: '#FCA5A5', icon: '✕', title: 'Request Rejected Successfully' },
  viewed:   { bg: '#DBEAFE', fg: '#1E3A8A', border: '#93C5FD', icon: '👁', title: 'Request Details' },
  expired:  { bg: '#FEF3C7', fg: '#92400E', border: '#FCD34D', icon: '⏰', title: 'Approval Link Expired' },
  invalid:  { bg: '#FEE2E2', fg: '#991B1B', border: '#FCA5A5', icon: '⚠', title: 'Invalid Link' },
  error:    { bg: '#FEE2E2', fg: '#991B1B', border: '#FCA5A5', icon: '⚠', title: 'Something Went Wrong' },
};

const RequestTypeLabels = {
  leave: 'Leave Request',
  correction: 'Clock-Out Correction',
  'fix-logout': 'Fix Logout Request',
  'course-price': 'Course Price Change',
};

function getModuleUrl(requestType) {
  switch (requestType) {
    case 'course-price':
      return `${window.location.origin}/courses?tab=requests`;
    case 'leave':
      return `${window.location.origin}/leave`;
    case 'correction':
    case 'fix-logout':
      return `${window.location.origin}/work-attendance`;
    default:
      return `${window.location.origin}/dashboard`;
  }
}

function mapApprovalError(data, status) {
  const message = data?.message || 'Invalid or expired link.';
  const msg = message.toLowerCase();
  if (status === 404 || msg.includes('not found')) {
    return { severity: 'invalid', message: 'Request not found or already processed.' };
  }
  if (status === 409 || msg.includes('already approved') || msg.includes('already rejected') || msg.includes('already')) {
    return { severity: 'error', message };
  }
  if (msg.includes('expired')) {
    return { severity: 'expired', message };
  }
  return { severity: 'invalid', message };
}

function unwrapApiPayload(response) {
  if (!response || typeof response !== 'object') return response;
  return response.data && typeof response.data === 'object' ? response.data : response;
}

export default function RequestApprovalPage() {
  const { action, token } = useParams();
  // The URL pattern is /request-approval/{action}/{token}; legacy
  // /attendance/request/{action}/{token} paths are still accepted.
  const routeAction = (action || 'view').toLowerCase();
  const isLinkRoute = routeAction === 'link';
  const [intendedAction, setIntendedAction] = useState(null);
  const [requestMeta, setRequestMeta] = useState(null);
  const resolvedAction = routeAction === 'view'
    ? 'view'
    : (isLinkRoute ? intendedAction : (intendedAction || routeAction));
  const isApproval = ['approve', 'reject'].includes(resolvedAction);

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (routeAction !== 'view') {
          const url = `${API_URL}/training/approvals/inspect/${token}`;
          const r = await fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' } });
          const data = await r.json().catch(() => ({}));
          console.log('[approval-page] inspect', { url, status: r.status, ok: r.ok, data });
          if (cancelled) return;
          if (!r.ok) {
            setError(mapApprovalError(data, r.status));
          } else {
            const payload = unwrapApiPayload(data);
            setIntendedAction((payload?.intendedAction || '').toLowerCase());
            setRequestMeta(payload || null);
            setResult(null);
          }
        }
        if (routeAction === 'view') {
          const url = `${API_URL}/training/approvals/${resolvedAction}/${token}`;
          const r = await fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' } });
          const data = await r.json().catch(() => ({}));
          console.log('[approval-page] view', { url, status: r.status, ok: r.ok, data });
          if (cancelled) return;
          if (!r.ok) {
            setError(mapApprovalError(data, r.status));
          } else {
            setResult(unwrapApiPayload(data));
          }
        }
      } catch (e) {
        if (cancelled) return;
        setError({ severity: 'error', message: e.message || 'Network error.' });
      }
      setLoading(false);
    };
    run();
    return () => { cancelled = true; };
  }, [token, resolvedAction, isLinkRoute, routeAction]);

  const submitDecision = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const url = `${API_URL}/training/approvals/${resolvedAction}/${token}`;
      const r = await fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' } });
      const data = await r.json().catch(() => ({}));
      console.log('[approval-page] submit', { url, status: r.status, ok: r.ok, data, resolvedAction });
      if (!r.ok) {
        setError(mapApprovalError(data, r.status));
        setSubmitting(false);
        return;
      }
      setResult(unwrapApiPayload(data));
    } catch (e) {
      setError({ severity: 'error', message: e.message || 'Network error.' });
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <Centered>
        <Spinner />
        <p style={{ marginTop: 16, color: '#475569', fontSize: 14 }}>
          {isApproval ? 'Processing your decision…' : 'Loading request…'}
        </p>
      </Centered>
    );
  }

  if (isLinkRoute && !error && !resolvedAction) {
    return (
      <Centered>
        <Spinner />
        <p style={{ marginTop: 16, color: '#475569', fontSize: 14 }}>
          Preparing approval link...
        </p>
      </Centered>
    );
  }

  // Error states (expired / invalid / network)
  if (error) {
    const cfg = SEVERITY[error.severity] || SEVERITY.error;
    return (
      <Centered>
        <ResultCard severity={cfg} title={cfg.title} message={error.message}>
          <p style={{ color: '#64748B', fontSize: 13, margin: '14px 0 0' }}>
            Please log in to the system to take action on this request.
          </p>
          <LoginButton />
        </ResultCard>
      </Centered>
    );
  }

  // Success states — approval is automatically applied, no login required.
  if (isApproval) {
    if (!result) {
      const cfg = resolvedAction === 'approve'
        ? { bg: '#DCFCE7', fg: '#166534', border: '#86EFAC', icon: '✓', title: 'Confirm Approval' }
        : { bg: '#FEE2E2', fg: '#991B1B', border: '#FCA5A5', icon: '✕', title: 'Confirm Rejection' };
      const actionLabel = resolvedAction === 'approve' ? 'approve' : 'reject';
      return (
        <Centered>
          <ResultCard severity={cfg} title={cfg.title}>
            <p style={{ color: '#475569', fontSize: 15, margin: '14px 0' }}>
              This email link is ready to <strong style={{ color: cfg.fg }}>{actionLabel}</strong> the request.
            </p>
            <p style={{ color: '#64748B', fontSize: 13, margin: '14px 0' }}>
              The action will only be applied after you confirm below.
            </p>
            {error && (
              <div style={{
                marginTop: 12, padding: '12px 14px', borderRadius: 8,
                background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B',
                fontSize: 13,
              }}>
                {error.message}
              </div>
            )}
            <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={submitDecision}
                disabled={submitting}
                style={{
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: 8,
                  background: cfg.fg,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: submitting ? 'default' : 'pointer',
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? 'Processing...' : (resolvedAction === 'approve' ? 'Confirm Approve' : 'Confirm Reject')}
              </button>
              <a
                href={getModuleUrl(result?.requestType || requestMeta?.requestType)}
                style={{
                  display: 'inline-block',
                  padding: '12px 24px',
                  borderRadius: 8,
                  background: '#E2E8F0',
                  color: '#334155',
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                Open Module
              </a>
            </div>
          </ResultCard>
        </Centered>
      );
    }

    const cfg = SEVERITY[result?.newStatus] || SEVERITY.approved;
    const requestTypeLabel = RequestTypeLabels[result?.requestType] || 'request';
    return (
      <Centered>
        <ResultCard severity={cfg} title={cfg.title}>
          <p style={{ color: '#475569', fontSize: 15, margin: '14px 0' }}>
            The <strong>{requestTypeLabel}</strong> has been <strong style={{ color: cfg.fg }}>{result?.newStatus}</strong> automatically.
          </p>
          <p style={{ color: '#64748B', fontSize: 13, margin: '14px 0' }}>
            No login required — the action was completed using your secure email link.
            The employee will be notified by email about this decision.
          </p>
          {/* Optional login link — does NOT redirect automatically */}
          <p style={{ marginTop: 18, fontSize: 12, color: '#94A3B8' }}>
            Want to verify in the system?{' '}
            <a href={getModuleUrl(result?.requestType || requestMeta?.requestType)} style={{ color: '#3B82F6', textDecoration: 'underline' }}>
              Open module
            </a>
          </p>
        </ResultCard>
      </Centered>
    );
  }

  // View request — no login required, just show the details
  if (resolvedAction === 'view' && result?.request) {
    const r = result.request;
    const employeeName = r.employee ? `${r.employee.firstName || ''} ${r.employee.lastName || ''}`.trim() : '—';
    return (
      <Centered>
        <ResultCard severity={SEVERITY.viewed} title={RequestTypeLabels[result.requestType] || 'Request Details'}>
          <p style={{ color: '#64748B', fontSize: 13, margin: '0 0 14px' }}>
            This is a read-only view. To approve or reject, use the buttons in the original email.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            {r.employee && (
              <DetailRow label="Employee" value={employeeName} />
            )}
            {r.leaveType && <DetailRow label="Leave Type" value={r.leaveType} />}
            {r.leaveDuration && <DetailRow label="Duration" value={`${r.leaveDuration}${r.totalDays ? ` (${r.totalDays} day${r.totalDays > 1 ? 's' : ''})` : ''}${r.permissionHours ? ` (${r.permissionHours} hr)` : ''}`} />}
            {r.startDate && <DetailRow label="Start Date" value={fmtDate(r.startDate)} />}
            {r.endDate && <DetailRow label="End Date" value={fmtDate(r.endDate)} />}
            {r.attendanceDate && <DetailRow label="Attendance Date" value={fmtDate(r.attendanceDate)} highlight />}
            {r.originalClockInTime && <DetailRow label="Original Clock-In" value={fmtTime(r.originalClockInTime)} />}
            {r.lastActivityTime && <DetailRow label="Last Activity" value={fmtDateTime(r.lastActivityTime)} />}
            {r.requestedLogoutTime && <DetailRow label="Requested Logout" value={fmtDateTime(r.requestedLogoutTime)} highlight />}
            {r.currentPricing && <DetailRow label="Current Fees" value={`₹${r.currentPricing.totalFees}`} />}
            {r.requestedPricing && <DetailRow label="Requested Fees" value={`₹${r.requestedPricing.totalFees}`} highlight />}
            {r.reason && <DetailRow label="Reason" value={r.reason} multiline />}
            <DetailRow label="Status" value={<span style={{ fontWeight: 700, color: statusColor(r.status) }}>{r.status.toUpperCase()}</span>} />
            <DetailRow label="Submitted" value={fmtDateTime(r.createdAt)} muted />
          </table>
          {/* Optional login link — does NOT redirect automatically */}
          <p style={{ marginTop: 18, fontSize: 12, color: '#94A3B8' }}>
            Want to verify in the system?{' '}
            <a href={getModuleUrl(result?.requestType || requestMeta?.requestType)} style={{ color: '#3B82F6', textDecoration: 'underline' }}>
              Open module
            </a>
          </p>
        </ResultCard>
      </Centered>
    );
  }

  // Fallback
  return (
    <Centered>
      <ResultCard severity={SEVERITY.invalid} title="Unexpected Response">
        <p style={{ color: '#475569', fontSize: 14 }}>
          The server returned an unexpected response. Please log in to view this request.
        </p>
        <LoginButton small />
      </ResultCard>
    </Centered>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function Centered({ children }) {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#F1F5F9', padding: '24px 16px', fontFamily: 'Arial, sans-serif',
    }}>
      <div style={{ maxWidth: 540, width: '100%' }}>{children}</div>
    </div>
  );
}

function Spinner() {
  return (
    <div className="spinner-border" role="status" style={{ width: 48, height: 48, color: '#3B82F6' }}>
      <span className="visually-hidden">Loading…</span>
    </div>
  );
}

function ResultCard({ severity, title, message, children }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, overflow: 'hidden',
      boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
      border: `1px solid ${severity.border}`,
    }}>
      <div style={{
        background: severity.bg, padding: '20px 28px',
        borderBottom: `1px solid ${severity.border}`,
        display: 'flex', alignItems: 'center', gap: 14,
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, fontWeight: 800, color: severity.fg,
          boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
        }}>
          {severity.icon}
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, color: severity.fg, fontWeight: 800 }}>{title}</h1>
          {message && <p style={{ margin: '4px 0 0', fontSize: 13, color: severity.fg, opacity: 0.85 }}>{message}</p>}
        </div>
      </div>
      <div style={{ padding: '24px 28px' }}>{children}</div>
    </div>
  );
}

function DetailRow({ label, value, highlight, multiline, muted }) {
  return (
    <tr>
      <td style={{
        padding: '8px 0', color: '#64748B', fontSize: 13, width: '42%',
        verticalAlign: 'top', fontWeight: 500,
      }}>{label}</td>
      <td style={{
        padding: '8px 0', color: muted ? '#94A3B8' : (highlight ? '#1E3A8A' : '#0F172A'),
        fontSize: highlight ? 15 : 14, fontWeight: highlight ? 700 : 500,
        whiteSpace: multiline ? 'pre-wrap' : 'normal',
      }}>{value || '—'}</td>
    </tr>
  );
}

function LoginButton({ small }) {
  // Use FRONTEND_URL (window.location.origin) as the login destination.
  // The login page knows how to redirect back to the relevant module
  // after successful authentication.
  const loginUrl = `${window.location.origin}/login`;
  return (
    <div style={{ marginTop: small ? 16 : 24, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      <a
        href={loginUrl}
        style={{
          display: 'inline-block', padding: small ? '8px 18px' : '12px 24px',
          background: '#1E3A8A', color: '#fff', textDecoration: 'none',
          borderRadius: 8, fontWeight: 600, fontSize: small ? 13 : 14,
        }}
      >
        <i className="bi bi-box-arrow-in-right me-1"></i> Go to Login
      </a>
    </div>
  );
}

// ─── Formatters ─────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}
function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
}
function statusColor(s) {
  if (s === 'approved') return '#166534';
  if (s === 'rejected') return '#991B1B';
  if (s === 'proposed' || s === 'pending') return '#92400E';
  return '#1E3A8A';
}
